import React from 'react';
import { Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExtendedProcessingData } from '@/types/consolidation-types';

interface ShippingFlowProps {
  shippingFlow?: ExtendedProcessingData['shippingFlow'] | null;
  className?: string;
}

// Generated data can contain missing-value strings as well as null scalars.
function suppliedValue(value: unknown, pendingIsMissing = false): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || /^(?:unknown(?: carrier)?|not (?:specified|provided|available|recorded)|n\/?a|none|null|undefined|tbd|invalid date|[-–—?.]+)$/i.test(text)) return null;
  if (pendingIsMissing && /^pending$/i.test(text)) return null;
  return text;
}

function suppliedDate(value: unknown): string | null {
  const text = suppliedValue(value, true);
  if (!text) return null;
  // Only valid calendar dates are reformatted; other generated values remain unverified.
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const date = new Date(`${text}T00:00:00Z`);
    if (Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === text) {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
  }
  return `${text} (unverified value)`;
}

function Endpoint({ label, organisation, location }: { label: string; organisation: unknown; location: unknown }) {
  const name = suppliedValue(organisation);
  const place = suppliedValue(location);
  return <div className="grid min-w-0 grid-cols-[3rem_minmax(0,1fr)] gap-3">
    <dt className="pt-0.5 text-xs text-muted-foreground">{label}</dt>
    <dd className="min-w-0 break-words text-sm">
      <p className="font-medium">{name || place || 'Not available'}</p>
      {name && place && <p className="mt-0.5 text-muted-foreground">{place}</p>}
    </dd>
  </div>;
}

export function ShippingFlow({ shippingFlow, className }: ShippingFlowProps) {
  const headingId = React.useId();
  const route = Array.isArray(shippingFlow?.route) ? shippingFlow.route
    .filter(leg => leg && typeof leg === 'object')
    .slice().sort((a, b) => (Number.isFinite(a.sequence) ? a.sequence : Infinity) - (Number.isFinite(b.sequence) ? b.sequence : Infinity)) : [];
  const documentStatus = suppliedValue(shippingFlow?.currentStatus);
  const packageFields = [
    ['Weight', suppliedValue(shippingFlow?.packageDetails?.weight)],
    ['Dimensions', suppliedValue(shippingFlow?.packageDetails?.dimensions)],
    ['Contents', suppliedValue(shippingFlow?.packageDetails?.contents)],
  ];
  const missingPackageFields = packageFields.filter(([, value]) => !value).map(([label]) => label.toLowerCase());

  return <section aria-labelledby={headingId} className={cn('rounded-lg border bg-card text-card-foreground', className)}>
    <div className="space-y-4 p-4 sm:p-5">
      <div>
        <h2 id={headingId} className="flex items-center gap-2 text-base font-semibold"><Truck aria-hidden="true" className="h-4 w-4 text-primary" />Shipping information</h2>
        <p className="mt-1 text-sm text-muted-foreground">AI-generated shipping summary. Details may be inferred; check the original documents.</p>
      </div>
      {documentStatus && <p className="text-sm"><span className="text-muted-foreground">Generated status: </span>{documentStatus}</p>}

      {route.length ? <ol className="divide-y">
        {route.map((leg, index) => {
          const legStatus = suppliedValue(leg.status);
          const facts = [
            ['Carrier', suppliedValue(leg.carrier)],
            ['Method', suppliedValue(leg.method)],
            ['Departure', suppliedDate(leg.departureDate)],
            ['Arrival', suppliedDate(leg.arrivalDate)],
            ['Tracking', suppliedValue(leg.trackingNumber, true)],
          ];
          const missing = facts.filter(([, value]) => !value).map(([label]) => label.toLowerCase());
          const documents = Array.isArray(leg.documents) ? leg.documents.map(value => suppliedValue(value)).filter(Boolean) : [];
          return <li key={index} className="space-y-3 py-4 first:pt-0 last:pb-0">
            {route.length > 1 && <h3 className="text-xs font-medium text-muted-foreground">Route leg {index + 1}</h3>}
            <dl className="space-y-3 border-l-2 border-primary/20 pl-3 sm:pl-4">
              <Endpoint label="From" organisation={leg.fromOrganization} location={leg.fromLocation} />
              <Endpoint label="To" organisation={leg.toOrganization} location={leg.toLocation} />
            </dl>
            <dl className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
              {facts.filter(([, value]) => value).map(([label, value]) => <div key={label} className="min-w-0 max-w-full">
                <dt className="text-muted-foreground">{label}</dt><dd className="mt-0.5 break-words">{value}</dd>
              </div>)}
              {legStatus && legStatus.toLowerCase() !== documentStatus?.toLowerCase() && <div><dt className="text-muted-foreground">Generated leg status</dt><dd className="mt-0.5">{legStatus}</dd></div>}
            </dl>
            {missing.length > 0 && <p className="text-xs text-muted-foreground">Not available in this summary: {missing.join(', ')}.</p>}
            {documents.length > 0 && <p className="break-words text-xs text-muted-foreground">Referenced documents: {documents.join(', ')}.</p>}
          </li>;
        })}
      </ol> : <p className="text-sm text-muted-foreground">No shipping route is available in this summary.</p>}

      <div className="space-y-2 border-t pt-3">
        <h3 className="text-sm font-medium">Package</h3>
        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          {packageFields.filter(([, value]) => value).map(([label, value]) => <div key={label} className="min-w-0 max-w-full"><dt className="text-muted-foreground">{label}</dt><dd className="mt-0.5 break-words">{value}</dd></div>)}
        </dl>
        {missingPackageFields.length > 0 && <p className="text-xs text-muted-foreground">Not available in this summary: {missingPackageFields.join(', ')}.</p>}
      </div>
    </div>
  </section>;
}
